import { useMemo, useState } from 'react';

const WEEKDAYS = [
  { value: 'monday', label: '월요일', shortLabel: '월' },
  { value: 'tuesday', label: '화요일', shortLabel: '화' },
  { value: 'wednesday', label: '수요일', shortLabel: '수' },
  { value: 'thursday', label: '목요일', shortLabel: '목' },
  { value: 'friday', label: '금요일', shortLabel: '금' },
];

const NOWON_BUS_TIMES = [
  '08:00', '08:10', '08:20', '08:30', '08:40', '08:50',
  '09:00', '09:10', '09:20', '10:00', '10:10', '10:20',
  '10:30', '10:40', '10:50', '11:00', '11:30', '12:00',
];

const SEAT_PREFERENCES = [11, 12, 15, 16, 19];
const DAY_VALUES_BY_INDEX = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
};
const DAY_INDEX_BY_VALUE = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
};

function getNextSchoolDay() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  if (date.getDay() === 6) {
    date.setDate(date.getDate() + 2);
  } else if (date.getDay() === 0) {
    date.setDate(date.getDate() + 1);
  }
  return date;
}

function getNextDateForDay(dayValue) {
  const date = new Date();
  const targetDay = DAY_INDEX_BY_VALUE[dayValue];
  let daysUntilTarget = (targetDay - date.getDay() + 7) % 7;
  if (daysUntilTarget === 0) daysUntilTarget = 7;
  date.setDate(date.getDate() + daysUntilTarget);
  return date;
}

function formatScheduleDate(date) {
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  });
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}

function StatCard({ label, value, detail, accent }) {
  const accentClasses = {
    blue: 'bg-tayo-light text-tayo',
    green: 'bg-emerald-50 text-emerald-600',
    violet: 'bg-violet-50 text-violet-600',
  };

  return (
    <div className="bg-white rounded-2xl shadow-card p-5">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-4 ${accentClasses[accent]}`}>
        <CheckIcon />
      </div>
      <p className="text-sm text-sub-text">{label}</p>
      <p className="text-2xl font-extrabold text-charcoal mt-1">{value}</p>
      <p className="text-xs text-sub-text mt-1">{detail}</p>
    </div>
  );
}

export default function AdminTaekyung() {
  const [selectedDay, setSelectedDay] = useState(
    () => DAY_VALUES_BY_INDEX[getNextSchoolDay().getDay()] || 'monday'
  );
  const selectedDayInfo = WEEKDAYS.find((day) => day.value === selectedDay);
  const nextSchoolDay = useMemo(getNextSchoolDay, []);
  const nextSchoolDayLabel = formatScheduleDate(nextSchoolDay);
  const selectedScheduleDate = useMemo(() => getNextDateForDay(selectedDay), [selectedDay]);
  const selectedScheduleDateLabel = formatScheduleDate(selectedScheduleDate);

  const schedules = useMemo(
    () => NOWON_BUS_TIMES.map((time, index) => ({
      id: `${selectedDay}-${time}`,
      time,
      preferredSeats: SEAT_PREFERENCES.map((seat) => seat + (index % 2)),
    })),
    [selectedDay]
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-charcoal">태경이 학교보내기</h2>
            <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">
              예약 대기
            </span>
          </div>
          <p className="text-sm text-sub-text mt-1">이태경 계정 노원 등교 자동예약 현황</p>
        </div>
        <span className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          다음 예약 {nextSchoolDayLabel}
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl bg-gradient-to-r from-tayo to-blue-600 shadow-card">
        <div className="p-6 sm:p-7 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 text-white flex items-center justify-center text-xl font-extrabold ring-1 ring-white/30">
              이
            </div>
            <div>
              <p className="text-lg font-bold text-white">이태경</p>
              <p className="text-sm text-white/70">20241432</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-white/85">
                <span className="rounded-full bg-white/15 px-2.5 py-1">등교</span>
                <span className="rounded-full bg-white/15 px-2.5 py-1">노원역 → 대진대학교</span>
              </div>
            </div>
          </div>
          <div className="sm:text-right">
            <p className="text-xs text-white/60">자동예약 적용 범위</p>
            <p className="text-2xl font-extrabold text-white mt-1">월요일 - 금요일</p>
            <p className="text-sm text-white/70 mt-1">노원 등교 전 시간대</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="등록 스케줄" value="90개" detail="주 5일 × 18개 시간대" accent="blue" />
        <StatCard label="다음 예약 예정" value="18개" detail={nextSchoolDayLabel} accent="green" />
        <StatCard label="예약 대상 시간대" value="18개" detail="08:00부터 12:00까지" accent="violet" />
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="font-bold text-charcoal">요일별 자동예약 스케줄</h3>
            <p className="text-xs text-sub-text mt-1">노원역 승차 · 등교 노선 · 내일 자동예약 예정</p>
          </div>
          <div className="flex gap-1 rounded-xl border border-border bg-snow p-1 overflow-x-auto">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => setSelectedDay(day.value)}
                className={`min-w-11 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                  selectedDay === day.value
                    ? 'bg-tayo text-white shadow-sm'
                    : 'text-sub-text hover:bg-white hover:text-charcoal'
                }`}
              >
                <span className="sm:hidden">{day.shortLabel}</span>
                <span className="hidden sm:inline">{day.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 bg-tayo-light/50 border-b border-border flex items-center justify-between">
          <p className="text-sm font-semibold text-tayo">{selectedDayInfo?.label} 등록 현황</p>
          <p className="text-xs text-sub-text">
            <span className="font-bold text-charcoal">{schedules.length}</span>개 예약 대기
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-snow">
              <tr>
                {['상태', '예약 예정일', '요일', '방향', '노선', '출발 시간', '승차 정류장', '선호 좌석'].map((heading) => (
                  <th key={heading} className="px-5 py-3 text-left text-xs font-medium text-charcoal whitespace-nowrap">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-white">
              {schedules.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-snow transition-colors">
                  <td className="px-5 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      예약 예정
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm font-semibold text-charcoal whitespace-nowrap">
                    {selectedScheduleDateLabel}
                  </td>
                  <td className="px-5 py-3 text-sm font-medium text-charcoal whitespace-nowrap">
                    {selectedDayInfo?.label}
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <span className="inline-flex rounded-full bg-tayo-light px-2.5 py-1 text-xs font-bold text-tayo">
                      등교
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm font-medium text-charcoal whitespace-nowrap">노원</td>
                  <td className="px-5 py-3 text-sm font-bold text-charcoal whitespace-nowrap">{schedule.time}</td>
                  <td className="px-5 py-3 text-sm text-charcoal whitespace-nowrap">노원역</td>
                  <td className="px-5 py-3 text-sm text-sub-text whitespace-nowrap">
                    {schedule.preferredSeats.join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <p className="text-xs text-sub-text">계정: 이태경 (20241432)</p>
          <p className="text-sm text-sub-text">
            총 <span className="font-bold text-charcoal">{schedules.length}</span>개 스케줄
          </p>
        </div>
      </div>
    </div>
  );
}
